import { createFileRoute } from "@tanstack/react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { Plus, Pencil } from "lucide-react"
import { toast } from "sonner"
import { api, type ApiEnvelope, type Stat } from "@/lib/api"
import { getErrorMessage, getFieldErrors } from "@/lib/get-error-message"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { FieldError } from "@/components/ui/field-error"
import { Switch } from "@/components/ui/switch"
import { Badge } from "@/components/ui/badge"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog"
import { ConfirmDelete } from "@/components/admin/confirm-delete"

export const Route = createFileRoute("/admin/_authed/stats")({
  component: StatsPage,
})

type FormState = {
  label: string
  value: string
  icon: string
  order: number
  is_active: boolean
}
const empty: FormState = { label: "", value: "", icon: "", order: 0, is_active: true }

function StatsPage() {
  const queryClient = useQueryClient()
  const { data, isLoading, isError } = useQuery<Stat[]>({
    queryKey: ["admin", "stats"],
    queryFn: async () => (await api.get<ApiEnvelope<Stat[]>>("/admin/stats")).data.data,
  })

  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState<Stat | null>(null)
  const [form, setForm] = useState<FormState>(empty)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["admin", "stats"] })
    queryClient.invalidateQueries({ queryKey: ["stats"] })
  }

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        label: form.label,
        value: form.value,
        icon: form.icon,
        order: form.order,
        is_active: form.is_active,
      }
      if (editing) {
        await api.put(`/admin/stats/${editing.id}`, payload)
      } else {
        await api.post("/admin/stats", payload)
      }
    },
    onSuccess: () => {
      invalidate()
      setOpen(false)
      setFieldErrors({})
      toast.success(editing ? "Stat updated." : "Stat created.")
    },
    onError: (error) => {
      setFieldErrors(getFieldErrors(error))
      toast.error(getErrorMessage(error))
    },
  })

  const remove = useMutation({
    mutationFn: async (id: number) => api.delete(`/admin/stats/${id}`),
    onSuccess: () => {
      invalidate()
      toast.success("Stat deleted.")
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const openCreate = () => {
    setEditing(null)
    setForm(empty)
    setFieldErrors({})
    setOpen(true)
  }
  const openEdit = (item: Stat) => {
    setEditing(item)
    setForm({
      label: item.label,
      value: item.value,
      icon: item.icon ?? "",
      order: item.order,
      is_active: item.is_active,
    })
    setFieldErrors({})
    setOpen(true)
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold">Stats</h1>
          <p className="mt-1 text-sm text-muted-foreground">Counters shown on the about page (e.g. "300+ Projects").</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreate} className="rounded-full">
              <Plus className="h-4 w-4" /> New stat
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editing ? "Edit stat" : "New stat"}</DialogTitle>
            </DialogHeader>
            <form
              className="space-y-4"
              noValidate
              onSubmit={(e) => {
                e.preventDefault()
                save.mutate()
              }}
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="value">Value</Label>
                  <Input
                    id="value"
                    placeholder="e.g. 300+"
                    value={form.value}
                    onChange={(e) => setForm({ ...form, value: e.target.value })}
                    aria-invalid={!!fieldErrors.value}
                  />
                  <FieldError message={fieldErrors.value} />
                </div>
                <div>
                  <Label htmlFor="label">Label</Label>
                  <Input
                    id="label"
                    placeholder="e.g. Projects delivered"
                    value={form.label}
                    onChange={(e) => setForm({ ...form, label: e.target.value })}
                    aria-invalid={!!fieldErrors.label}
                  />
                  <FieldError message={fieldErrors.label} />
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="icon">Icon (optional)</Label>
                  <Input id="icon" placeholder="e.g. rocket" value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} />
                </div>
                <div>
                  <Label htmlFor="order">Order</Label>
                  <Input
                    id="order"
                    type="number"
                    value={form.order}
                    onChange={(e) => setForm({ ...form, order: Number(e.target.value) })}
                  />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} id="is_active" />
                <Label htmlFor="is_active">Active</Label>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={save.isPending}>
                  {save.isPending ? "Saving..." : "Save"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-8">
        {isLoading ? (
          <div className="h-40 animate-pulse rounded-2xl bg-secondary" />
        ) : isError ? (
          <p className="text-sm text-destructive">Couldn't load stats.</p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {(data ?? []).map((s) => (
              <div key={s.id} className="rounded-2xl border border-border bg-card p-6 text-center">
                <div className="font-display text-3xl text-rust">{s.value}</div>
                <p className="mt-2 text-xs text-muted-foreground">{s.label}</p>
                <Badge className="mt-3" variant={s.is_active ? "default" : "secondary"}>
                  {s.is_active ? "Active" : "Hidden"}
                </Badge>
                <div className="mt-4 flex justify-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => openEdit(s)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <ConfirmDelete itemLabel={s.label} onConfirm={() => remove.mutate(s.id)} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
